import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useParams, Link } from "react-router-dom";
import PageTitle from "../../components/ui/PageTitle";
import { Headphones } from "@phosphor-icons/react";
import BackButton from "../../components/ui/BackButton";
import SurahSearch from "../../components/ui/SurahSearch"; // see tweak below

type Surah = { number: number; name: string };
const RECITER_NAME_MAP: Record<string,string> = {
  "ar.minshawi":"محمد المنشاوي","ar.kazemi":"عامر الكاظمي","ar.kjalil":"خالد الجليل","ar.mrashad":"محمد رشاد",
  "ar.maher":"ماهر المعيقلي","ar.sghamdi":"سعد الغامدي","ar.ajmy":"أحمد العجمي","ar.lahidan":"محمد اللحيدان","ar.mrayan":"محمد ريان",
};

export default function ByReciterPage() {
  const { reciterId = "" } = useParams();
  const reciter = decodeURIComponent(reciterId);
  const reciterName = RECITER_NAME_MAP[reciter] ?? reciter;

  const [surahs, setSurahs] = useState<Surah[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `${reciterName} | نور على نور`;
  }, [reciterName]);

  useEffect(() => {
    let m = true;
    fetch("https://api.alquran.cloud/v1/surah")
      .then(r => r.json())
      .then(d => m && setSurahs(d?.data ?? []))
      .finally(() => m && setLoading(false));
    return () => { m = false; };
  }, []);

  const data = useMemo(() => surahs, [surahs]);

  return (
    <GradientBg>
      <Container>
        <TopBar>
          <BackButton href="/listen" />
          <PageTitle icon={<Headphones size={22} weight="duotone" style={{ marginInlineEnd: 8 }} color="var(--primary)" />} center gap={8} as="h1">
            السور بصوت: {reciterName}
          </PageTitle>
        </TopBar>

        <Subtitle>اختر السورة للاستماع</Subtitle>
        <SurahSearch
          surahs={data}
          hrefFor={(n) => `/listen/${encodeURIComponent(reciter)}/${n}`}
        />

        {loading ? (
          <Loader>جارٍ التحميل...</Loader>
        ) : (
          <List>
            {data.map((s) => (
              <Row key={s.number} to={`/listen/${encodeURIComponent(reciter)}/${s.number}`}>
                <Badge>{s.number}</Badge>
                <Name>{s.name}</Name>
              </Row>
            ))}
          </List>
        )}
      </Container>
    </GradientBg>
  );
}

const GradientBg = styled.div` min-height:100dvh; display:flex; flex-direction:column; `;
const Container = styled.div` padding:16px clamp(16px,4vw,32px) 28px; flex:1; `;
const TopBar = styled.div` position:relative; display:flex; align-items:center; justify-content:center; min-height:40px; margin-bottom:8px; width:100%; `;
const Subtitle = styled.p` text-align:center; margin:6px 0 16px; color:var(--muted); font-size:.95rem; `;
const Loader = styled.div` text-align:center; padding:40px 0; font-size:1.05rem; `;
const List = styled.div` display:flex; flex-direction:column; gap:10px; `;
const Row = styled(Link)` display:flex; align-items:center; gap:12px; padding:14px 12px; border-radius:14px; background:var(--surface); border:1px solid var(--border); box-shadow:var(--shadow-sm); text-decoration:none; transition: transform .12s ease, box-shadow .12s ease; &:hover{ transform:translateY(-2px); box-shadow:0 12px 20px rgba(0,0,0,.12); } `;
const Badge = styled.span` min-width:32px; height:32px; border-radius:16px; background:var(--primary); color:#fff; display:inline-flex; align-items:center; justify-content:center; font-weight:700; font-size:.95rem; `;
const Name = styled.span` flex:1; font-size:2rem; font-family:"Lateef"; color:var(--text); line-height:1; `;
